import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Lock, X, Check } from 'lucide-react';
import { CookieConsent } from '@/types';

interface CookieCustomizeModalProps {
  consent: CookieConsent;
  onSave: (updates: Partial<CookieConsent>) => void;
  onClose: () => void;
}

export const CookieCustomizeModal: React.FC<CookieCustomizeModalProps> = ({
  consent,
  onSave,
  onClose,
}) => {
  const [localState, setLocalState] = useState<CookieConsent>({ ...consent });
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Click outside modal container to close
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const toggleField = (key: keyof CookieConsent) => {
    if (key === 'essential') return; // Cannot toggle essential
    setLocalState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    onSave({
      functional: localState.functional,
      analytics: localState.analytics,
      aiPerformance: localState.aiPerformance,
    });
    onClose();
  };

  const ToggleSwitch = ({
    active = false,
    disabled = false,
    onChange,
  }: {
    active?: boolean;
    disabled?: boolean;
    onChange?: () => void;
  }) => {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onChange}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-start rounded-full transition-colors duration-200 outline-none ${
          disabled
            ? 'bg-white/10 cursor-not-allowed'
            : active
            ? 'bg-[#F26522]'
            : 'bg-white/20'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
            active ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75"
      onClick={handleOverlayClick}
      id="cookie-customize-overlay"
    >
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        transition={{ type: 'spring', damping: 26, stiffness: 220 }}
        className="relative w-full max-w-md bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-2xl p-6 md:p-8 text-left text-white"
        id="cookie-customize-modal"
      >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5 p-1"
            aria-label="Close settings"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Heading */}
          <div className="mb-6">
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#F26522] block mb-1 font-heading">
              Ledger 04-B
            </span>
            <h2 className="text-sm font-bold uppercase tracking-widest text-white font-heading">
              Cookie Preferences
            </h2>
            <div className="h-px bg-white/[0.06] w-full mt-4" />
          </div>

          {/* Content Body / Preferences List */}
          <div className="space-y-6 mb-8 max-h-[60vh] overflow-y-auto pr-1 no-scrollbar">
            {/* Essential */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white font-heading">
                    Essential
                  </span>
                  <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wide bg-white/5 border border-white/10 text-white/40 px-1.5 py-0.5 rounded">
                    <Lock className="w-2 h-2" /> Locked
                  </span>
                </div>
                <p className="text-[11px] text-white/60 font-medium leading-relaxed">
                  Required for secure Firestore logins, active billing sessions, and basic secure anti-CSRF state. Cannot be disabled.
                </p>
              </div>
              <div className="shrink-0 flex items-center h-5">
                <Check className="w-4 h-4 text-[#F26522] mr-2" />
                <ToggleSwitch active={true} disabled={true} />
              </div>
            </div>

            {/* Functional */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white block font-heading">
                  Functional
                </span>
                <p className="text-[11px] text-white/60 font-medium leading-relaxed">
                  Remember your dashboard canvas layouts, selected currency filters, regional variables, and customized startup discovery feeds.
                </p>
              </div>
              <div className="shrink-0 flex items-center h-5">
                <ToggleSwitch
                  active={localState.functional}
                  onChange={() => toggleField('functional')}
                />
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white block font-heading">
                  Analytics
                </span>
                <p className="text-[11px] text-white/60 font-medium leading-relaxed">
                  Anonymized metric indicators to gauge app loading latency and feature engagement, optimizing services for East African venture networks.
                </p>
              </div>
              <div className="shrink-0 flex items-center h-5">
                <ToggleSwitch
                  active={localState.analytics}
                  onChange={() => toggleField('analytics')}
                />
              </div>
            </div>

            {/* AI Performance */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white block font-heading">
                  AI Performance
                </span>
                <p className="text-[11px] text-white/60 font-medium leading-relaxed">
                  Maintains active model context queues during live sector intelligence scans, accelerating Gemini AI generation times.
                </p>
              </div>
              <div className="shrink-0 flex items-center h-5">
                <ToggleSwitch
                  active={localState.aiPerformance}
                  onChange={() => toggleField('aiPerformance')}
                />
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-3 border-t border-white/[0.06] pt-6">
            <button
              onClick={onClose}
              className="text-[9px] font-bold uppercase tracking-widest text-white/50 hover:text-white transition-colors py-2.5 px-4 font-heading cursor-pointer"
              id="cookie-customize-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="text-[9px] font-bold uppercase tracking-widest bg-[#F26522] hover:bg-[#F26522]/90 text-white rounded-lg py-2.5 px-6 transition-colors font-heading cursor-pointer"
              id="cookie-customize-save"
            >
              Save Preferences
            </button>
          </div>
        </motion.div>
    </motion.div>
  );
};
